import i18next from "i18next";
import { DEVICE_USAGE_TYPE, EXPLORE_BROTHER_DEVICES, FEATURE_NAMES, PRINTER_TYPE, PRINTER_TYPE_API_MAP, TYPE_OF_PRINTING } from "../../constants/AppConstants";
import { printerTypes } from "../../constants/PrinterTypes";
import { getPlanTranslation } from "../../constants/AppMethod";


export const getCPPrinterHeader = (printerTypeId) => {
    if (printerTypeId === PRINTER_TYPE.INKJET) {
        return i18next.t("HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.HEADER_INKJET");
    } else if (printerTypeId === PRINTER_TYPE.MONO_LASER || printerTypeId === PRINTER_TYPE.COLOUR_LASER) {
        return i18next.t("HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.HEADER_LASER");
    }
}

export const getSelectPrinterHeaderTranslation = (printerTypeId, hardwareFeatureFlags) => {
    if (printerTypeId === PRINTER_TYPE.INKJET) {
        return i18next.t("HARDWARE_SIGNUP.GENERIC.INKJET");
    } else if (printerTypeId === PRINTER_TYPE.MONO_LASER && hardwareFeatureFlags?.isHWOnly) {
        return i18next.t("HARDWARE_SIGNUP.GENERIC.MONO_LASER");
    } else if (printerTypeId === PRINTER_TYPE.COLOUR_LASER && (hardwareFeatureFlags?.isNPBOnly || hardwareFeatureFlags?.isBothEnabled)) {
        return i18next.t("HARDWARE_SIGNUP.GENERIC.LASER");
    }
}

export const getPrinterImage = (printerTypeFeatures, selectedPrinterTypeId) => {
    const printer = printerTypeFeatures?.find(pt => pt.printerTypeId?.toString() === selectedPrinterTypeId?.toString());
    return printer ? printer.deviceTypeImageUrl : null;
};

export const getPrinterFeatures = (printerTypeFeatures, selectedPrinterTypeId, servicePlan, deviceType, typeOfPrinting) => {
    const effectivePrinterTypeId = getEffectivePrinterTypeId(selectedPrinterTypeId, typeOfPrinting)
    const printer = printerTypeFeatures?.find(pt => pt.printerTypeId?.toString() === effectivePrinterTypeId?.toString());
    if (!printer) return [];

    const plankey = deviceType === DEVICE_USAGE_TYPE.HARDWARE_INCLUSIVE ? "hardwarePlans" : "newPrinterBundlePlans"
    const selectedPlans = filterPlanDetails(servicePlan, effectivePrinterTypeId, deviceType, plankey, typeOfPrinting)
    const priceMap = {};
    selectedPlans?.forEach(plan => {
        const featureId = plan?.featureId?.[0];
        if (featureId) {
            const originalPrice = Number(plan.price);
            const discount = Number(plan.discount?.[0]?.discountPercentage || 0);
            const discountId = plan.discount?.[0]?.planId;
            const hasDiscount = discount > 0;
            const discountedPriceRounded = hasDiscount
                ? ((Math.round(originalPrice * 100) * (100 - discount)) / 10000).toFixed(2)
                : originalPrice.toFixed(2);
            priceMap[featureId] = {
                price: hasDiscount && originalPrice.toFixed(2),
                discountedPrice: discountedPriceRounded,
                currency: plan.currency,
                model: plan.model,
                deviceImageUrl: plan.deviceImageUrl,
                planId: plan?.planId,
                name: plan?.name,
                discountId: discountId,
                printerTypeId: effectivePrinterTypeId
            }
        }
    })

    const sortedFeatures = printer.features
        .sort((a, b) => a.sortOrder - b.sortOrder);

    const isHW =
        deviceType === DEVICE_USAGE_TYPE.HARDWARE_INCLUSIVE;

    const featuresToUse = isHW
        ? sortedFeatures.filter(
            (feature) =>
                feature?.featureName === FEATURE_NAMES.A4_PRINT_SCAN_COPY_FAX
        )
        : sortedFeatures;


    return featuresToUse
        .map(feature => ({
            ...feature,
            price: priceMap[feature?.featureId]?.price,
            discountedPrice: priceMap[feature.featureId]?.discountedPrice,
            currency: priceMap[feature.featureId]?.currency,
            model: priceMap[feature.featureId]?.model,
            deviceImageUrl: priceMap[feature.featureId]?.deviceImageUrl,
            planId: priceMap[feature.featureId]?.planId,
            planName: priceMap[feature.featureId]?.name,
            discountId: priceMap[feature.featureId]?.discountId,
            printerTypeId: priceMap[feature.featureId]?.printerTypeId,
            deviceUsageType: deviceType
        }))

};


export const getFeatureIdFromFeature = (featureName, feature) => {
    const featureObj = feature?.find(f => f.featureName === featureName);
    const featureId = featureObj ? featureObj?.featureId : null;
    return featureId
}

export const filterPlanDetails = (servicePlan, selectedPrinterTypeId, deviceType, planKey, typeOfPrinting = null) => {
    const effectivePrinterTypeId = getEffectivePrinterTypeId(selectedPrinterTypeId, typeOfPrinting)

    const allowedType = PRINTER_TYPE_API_MAP[effectivePrinterTypeId];

    if (!allowedType) return [];
    const filteredPlanDetails = [];

    servicePlan?.forEach(product => {
        const plans = product?.[planKey];
        plans?.forEach(plan => {
            if (plan.type !== allowedType) return;
            plan.planDetails?.forEach(planDetail => {

                // Required only 300 and 500 pages basicPlan for HW
                if (
                    planKey === "basicPlans" &&
                    Number(deviceType) === DEVICE_USAGE_TYPE.HARDWARE_INCLUSIVE
                ) {
                    if (planDetail.planPages === "300" || planDetail.planPages === "500") {
                        filteredPlanDetails.push(planDetail);
                    }
                }
                else {
                    filteredPlanDetails.push(planDetail);
                }

            });

        });

    });

    return filteredPlanDetails;
};

export const getUniquePlanLengths = (filteredPlans) => {
    const planLengths = filteredPlans.map(plan => plan.planLength);
    return [...new Set(planLengths)].sort((a, b) => a - b);
};


export const sortFeaturesInDescendingOrder = (features = []) => {
    return [...features].sort((a, b) => b.sortOrder - a.sortOrder);
};

export const getPrinterTypeDetails = (printerTypeId) => {
    switch (printerTypeId) {
        case 1:
            return {
                description: i18next.t('HARDWARE_SIGNUP.CHOOSE_PRINTER_TYPE.INKJET_DESC'),
                info: i18next.t('HARDWARE_SIGNUP.CHOOSE_PRINTER_TYPE.INKJET_INFO'),
            };
        case 2:
        case 3:
            return {
                description: i18next.t('HARDWARE_SIGNUP.CHOOSE_PRINTER_TYPE.LASER_DESC'),
                info: i18next.t('HARDWARE_SIGNUP.CHOOSE_PRINTER_TYPE.LASER_INFO'),
            };
        default:
            return { description: '', info: '' };
    }
};

export const getHWPrinterTypeTranslation = (printerName, hardwareFeatureFlags) => {
    const printerTypesObj = printerTypes
    const PrinterTypeKey = Object.keys(printerTypesObj).find(key => printerTypesObj[key] === printerName);
    if (i18next.t(`CUSTOMER_WEB.SELECT_PRINTER.${PrinterTypeKey}`).includes('CUSTOMER_WEB.SELECT_PRINTER')) {
        return printerName
    }
    else {
        return i18next.t(`CUSTOMER_WEB.SELECT_PRINTER.${PrinterTypeKey}`)
    }
}

export const getHWPrinterFeatureTranslation = (featureId, featureName, selectedDeviceType, hardwareFeatureFlags = {}) => {
    const isHW = Number(selectedDeviceType) === DEVICE_USAGE_TYPE.HARDWARE_INCLUSIVE

    // REFURBISHED
    // Always fixed capabilities
    if (isHW) {

        return i18next.t(
            "HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.HARDWARE_TYPE.REFURBISHED.A4_PRINT_SCAN_COPY_FAX"
        );
    }
    // NEW PRINTER BUNDLE
    // Dynamic based on feature name
    let translationKey = "";

    switch (featureName) {

        case FEATURE_NAMES.A4_PRINT:
            translationKey = "A4_PRINT";
            break;

        case FEATURE_NAMES.A4_PRINT_SCAN_COPY:
            translationKey = "A4_PRINT_SCAN_COPY";
            break;

        case FEATURE_NAMES.A4_PRINT_SCAN_COPY_FAX:
            translationKey = "A4_PRINT_SCAN_COPY_FAX";
            break;

        case FEATURE_NAMES.A3_PRINT_SCAN_COPY_FAX:
            translationKey = "A3_PRINT_SCAN_COPY_FAX";
            break;

        default:
            return featureName;
    }
    return i18next.t(
        `HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.HARDWARE_TYPE.NEW_PRINTER_BUNDLE.${translationKey}`
    );
};

export const getHardwareFeatureFlags = (data = {}) => {
    const {
        HardwareInclusive = false,
        NewPrinterBundle = false
    } = data;

    return {
        isHWOnly: HardwareInclusive && !NewPrinterBundle,
        isNPBOnly: !HardwareInclusive && NewPrinterBundle,
        isBothEnabled: HardwareInclusive && NewPrinterBundle,
        isAnyEnabled: HardwareInclusive || NewPrinterBundle
    };
};

export const buildFormattedSelections = (featureSelections, printerTypeId, hardwareFeatureFlags) => {
    return {
        device: getSelectPrinterHeaderTranslation(printerTypeId, hardwareFeatureFlags),

        deviceType:
            Number(featureSelections?.deviceType) === DEVICE_USAGE_TYPE.NEW_PRINTER_BUNDLE
                ? i18next.t("HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.DEVICE_TYPE.NEW")
                : i18next.t("HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.DEVICE_TYPE.REFURBISHED"),

        hardwarePrinterType: getHWPrinterFeatureTranslation(featureSelections?.hardwarePrinterType?.featureId, featureSelections?.hardwarePrinterType?.featureName, featureSelections?.deviceType),

        monthlyPageAllowance: `${getPlanTranslation(featureSelections?.monthlyPageAllowance?.planName)} - ${i18next.t('ECOPROCAROUSEL_CONTENT.NO_OF_PAGES', { planPages: featureSelections?.monthlyPageAllowance?.planPages })}`,
        planLength: featureSelections?.planLength
            ? `${featureSelections.planLength} ${i18next.t("HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.PLAN_LENGTH.MONTHS")}`
            : null,

        typeOfPrinting: featureSelections?.typeOfPrinting
            ? i18next.t(
                featureSelections.typeOfPrinting === TYPE_OF_PRINTING.MONO
                    ? "HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.PRINT_OUTPUT.MONO"
                    : "HARDWARE_SIGNUP.CUSTOMIZE_PRINTER.PRINT_OUTPUT.COLOUR"
            )
            : null
    };
}

export const isCheckoutDisabled = (selections, printerTypeId) => {
    if (!selections?.deviceType) return true;

    if (!selections?.hardwarePrinterType?.featureId) return true;

    if (!selections?.monthlyPageAllowance?.planPages) return true;

    if (
        Number(selections?.deviceType) === DEVICE_USAGE_TYPE.HARDWARE_INCLUSIVE &&
        !selections?.planLength
    ) return true;

    if (
        (printerTypeId === PRINTER_TYPE.MONO_LASER || printerTypeId === PRINTER_TYPE.COLOUR_LASER) &&
        !selections?.typeOfPrinting
    ) return true;

    return false;
};

export const getEffectivePrinterTypeId = (printerTypeId, typeOfPrinting) => {

    if (Number(printerTypeId) === PRINTER_TYPE.INKJET) {
        return PRINTER_TYPE.INKJET;
    }

    if (typeOfPrinting === TYPE_OF_PRINTING.COLOUR) {
        return PRINTER_TYPE.COLOUR_LASER;
    }

    return PRINTER_TYPE.MONO_LASER;
};

// Locale-specific path configurations
const LOCALE_PATH_CONFIG = {
    "fr-FR": {
        devices: "appareils",
        inkjet: "jet-d-encre",
        laser: "laser"
    },
    "fr-BE": {
        devices: "appareils",
        inkjet: "jet-d-encre",
        laser: "laser"
    },
    "fr-CH": {
        devices: "appareils",
        inkjet: "jet-d-encre",
        laser: "laser"
    },
    "de-DE": {
        devices: "geraete",
        inkjet: "tintenstrahl",
        laser: "laser"
    },
    // Default for English and other locales
    default: {
        devices: "devices",
        inkjet: "inkjet",
        laser: "laser"
    }
};

const normalizePrinterType = (printerType) => {
    const normalized = printerType.toLowerCase().trim();

    if (normalized.includes("laser")) {
        return "laser";
    }

    if (normalized.includes("inkjet") || normalized.includes("ink")) {
        return "inkjet";
    }

    return normalized;
};


const extractModelSeries = (model) => {
    // Remove hyphens and convert to lowercase
    const normalized = model.toLowerCase().replace(/-/g, '');

    // Pattern matching based on our URL examples:
    // DCP-J1310DW -> dcpj1310dw -> dcpj
    // HL-L2400DWE -> hll2400dwe -> hl (not hll)
    // DCP-L2627DWE -> dcpl2627dwe -> dcp (not dcpl)
    // MFC-J4350DW -> mfcj4350dw -> mfcj
    // MFC-L2800DW -> mfcl2800dw -> mfc (not mfcl)

    const seriesMap = {
        mfcj: "mfcj",
        mfcl: "mfc",
        dcpj: "dcpj",
        dcpl: "dcp",
        hll: "hl"
    };

    const prefix = normalized.substring(0, 4);

    // sometimes for HLL series we need only first 3 chars of the model
    const hllPrefix = normalized.substring(0, 3);

    if (seriesMap[prefix]) {
        return seriesMap[prefix];
    } else if (seriesMap[hllPrefix]) {
        return seriesMap[hllPrefix]
    }

    // fallback (hl, mfc, dcp etc.)
    const match = normalized.match(/^[a-z]+/);
    return match ? match[0] : normalized;
};


export const generateDeviceURL = (locale, printerType, model) => {
    const baseURL = EXPLORE_BROTHER_DEVICES[locale];

    if (!baseURL) {
        return null;
    }

    // Get locale-specific path configuration
    const pathConfig = LOCALE_PATH_CONFIG[locale] || LOCALE_PATH_CONFIG.default;

    // Normalize printer type (colour laser, mono laser -> laser)
    const normalizedPrinterType = normalizePrinterType(printerType);

    // Get the appropriate printer type path
    const printerTypePath = pathConfig[normalizedPrinterType] || normalizedPrinterType;

    // Extract model series (e.g., "dcpj", "hl", "dcp", "mfcj", "mfc") from full model
    const modelSeries = extractModelSeries(model);

    // Convert model to lowercase and remove hyphens for the final path segment
    const modelPath = model.toLowerCase().replace(/-/g, '');

    // Construct the full URL
    const url = `${baseURL}/${pathConfig.devices}/${printerTypePath}/${modelSeries}/${modelPath}`;

    return url;
};

export const calculateLowestPrinterPrices = (
    hwProducts,
    npbProducts,
    hardwareFeatureFlags
) => {

    const getLowest = (plans = []) => {
        if (!plans.length) return null;

        return plans.reduce((min, p) =>
            parseFloat(p.price) < parseFloat(min.price) ? p : min
        );
    };

    const getBasicPlans = (products, type, source) => {
        let plans = [];

        products?.forEach(product => {
            product.basicPlans?.forEach(basic => {

                if (basic.type === type) {

                    let filtered = basic.planDetails;

                    // HW API → only 300 & 500 pages
                    if (source === "HW") {
                        filtered = basic.planDetails.filter(
                            p => p.planPages === "300" || p.planPages === "500"
                        );
                    }

                    plans.push(...filtered);
                }
            });
        });

        return getLowest(plans);
    };

    const getHWPlans = (products, type) => {
        let plans = [];

        products?.forEach(product => {
            product.hardwarePlans?.forEach(plan => {
                if (plan.type === type) {
                    plans.push(...plan.planDetails);
                }
            });
        });

        return getLowest(plans);
    };

    const getNPBPlans = (products, type) => {
        let plans = [];

        products?.forEach(product => {
            product.newPrinterBundlePlans?.forEach(plan => {
                if (plan.type === type) {
                    plans.push(...plan.planDetails);
                }
            });
        });

        return getLowest(plans);
    };

    let inkMonthly = null;
    let inkDueToday = null;

    let laserMonthly = null;
    let laserDueToday = null;

    // ---------------------------
    // HW ONLY
    // ---------------------------

    if (hardwareFeatureFlags?.isHWOnly) {

        inkMonthly = getBasicPlans(hwProducts, "InkJet", "HW");
        inkDueToday = getHWPlans(hwProducts, "InkJet");

        laserMonthly = getBasicPlans(hwProducts, "Mono Laser", "HW");
        laserDueToday = getHWPlans(hwProducts, "Mono Laser");
    }

    // ---------------------------
    // NPB ONLY
    // ---------------------------

    else if (hardwareFeatureFlags?.isNPBOnly) {

        inkMonthly = getBasicPlans(npbProducts, "InkJet", "NPB");
        inkDueToday = getNPBPlans(npbProducts, "InkJet");

        const monoMonthly = getBasicPlans(npbProducts, "Mono Laser", "NPB");
        const colourMonthly = getBasicPlans(npbProducts, "Colour Laser", "NPB");

        const monoDue = getNPBPlans(npbProducts, "Mono Laser");
        const colourDue = getNPBPlans(npbProducts, "Colour Laser");

        laserMonthly = getLowest([monoMonthly, colourMonthly].filter(Boolean));
        laserDueToday = getLowest([monoDue, colourDue].filter(Boolean));
    }

    // ---------------------------
    // BOTH ENABLED
    // ---------------------------

    else if (hardwareFeatureFlags?.isBothEnabled) {

        const hwInkMonthly = getBasicPlans(hwProducts, "InkJet", "HW");
        const npbInkMonthly = getBasicPlans(npbProducts, "InkJet", "NPB");

        const hwInkDue = getHWPlans(hwProducts, "InkJet");
        const npbInkDue = getNPBPlans(npbProducts, "InkJet");

        inkMonthly = getLowest([hwInkMonthly, npbInkMonthly].filter(Boolean));
        inkDueToday = getLowest([hwInkDue, npbInkDue].filter(Boolean));

        const monoMonthlyHW = getBasicPlans(hwProducts, "Mono Laser", "HW");
        const monoMonthlyNPB = getBasicPlans(npbProducts, "Mono Laser", "NPB");
        const colourMonthlyNPB = getBasicPlans(npbProducts, "Colour Laser", "NPB");

        const monoDueHW = getHWPlans(hwProducts, "Mono Laser");
        const monoDueNPB = getNPBPlans(npbProducts, "Mono Laser");
        const colourDueNPB = getNPBPlans(npbProducts, "Colour Laser");

        laserMonthly = getLowest([
            monoMonthlyHW,
            monoMonthlyNPB,
            colourMonthlyNPB
        ].filter(Boolean));

        laserDueToday = getLowest([
            monoDueHW,
            monoDueNPB,
            colourDueNPB
        ].filter(Boolean));
    }

    return {
        ink: {
            monthly: inkMonthly,
            dueToday: inkDueToday
        },
        laser: {
            monthly: laserMonthly,
            dueToday: laserDueToday
        }
    };
};






